// "Replace photo" picker: pick from the business's brand library (already in memory
// via the brand-kit store) or upload a new file. On pick we hand back the stable
// attachmentId (the caller stores it as a `{kind:'attachment'}` src) and seed the
// display-url cache so the canvas updates instantly with no round-trip.

import { ImagePickerTile, Popover, PopoverContent, PopoverTrigger } from 'components'
import { cn } from 'lib/utils'
import { ImagePlus, Loader2, UploadCloud } from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useBrandKitStore } from '../../store/brand-kit-store'
import { UploadError, useUploadFile } from '../../hooks/useUploadFile'
import { seedAttachmentUrl } from '../use-attachment-url'

const MAX_MB = 10

export function ReplacePhotoPopover({
  onPick,
  children,
  title = 'Replace photo',
}: {
  /** Called with the chosen photo's stable attachment id. */
  onPick: (attachmentId: string) => void
  /** The trigger element (a button). */
  children: ReactNode
  /** Header label — "Replace photo" by default, "Add photo" for the add-layer flow. */
  title?: string
}) {
  // Select the STABLE array ref and filter in a memo. Filtering inside the selector returns a
  // new array on every store read, which makes useSyncExternalStore think the snapshot changed
  // each render → "getSnapshot should be cached" infinite-loop warning + render thrash (which,
  // when this popover is mounted inline in the mobile layer toolbar, tore down the edit sheet).
  const allImages = useBrandKitStore(s => s.brandKit.images)
  const images = useMemo(() => allImages.filter(i => !!i.publicUrl), [allImages])
  const upload = useUploadFile()
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pick = (attachmentId: string, url?: string) => {
    if (url) seedAttachmentUrl(attachmentId, url)
    onPick(attachmentId)
    setOpen(false)
  }

  const handleFiles = async (files: FileList | null | undefined) => {
    const file = files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('That file is not an image.')
      return
    }
    setUploading(true)
    setError(null)
    try {
      const { attachmentId, publicUrl } = await upload(file, { maxBytes: MAX_MB * 1024 * 1024 })
      pick(attachmentId, publicUrl)
    } catch (err) {
      setError(
        err instanceof UploadError && err.reason === 'too_large'
          ? `That image is over the ${MAX_MB}MB limit.`
          : 'Upload failed. Please try again.'
      )
      console.error('ReplacePhotoPopover: upload failed', err)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        variant="dark"
        align="center"
        className="w-[320px] p-3"
        aria-label={title}
        onPointerDown={e => e.stopPropagation()}
      >
        <div className="mb-2.5 text-sm font-semibold text-paper">{title}</div>

        {/* Upload — always the primary action, doubles as a drop target. */}
        <label
          onDragOver={e => {
            e.preventDefault()
            setDragActive(true)
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={e => {
            e.preventDefault()
            setDragActive(false)
            void handleFiles(e.dataTransfer.files)
          }}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[10px] border-2 border-dashed px-3 py-5 text-center transition-colors',
            dragActive
              ? 'border-acid bg-acid/10 text-paper'
              : 'border-line-bright text-paper-dim hover:border-acid hover:text-paper',
            uploading && 'pointer-events-none opacity-70'
          )}
        >
          {uploading ? (
            <Loader2 size={20} className="animate-spin text-acid" />
          ) : (
            <UploadCloud size={20} />
          )}
          <span className="text-[13px] font-medium">
            {uploading ? 'Uploading…' : 'Upload a photo'}
          </span>
          <span className="text-[11px] text-paper-faint">
            {uploading ? 'Hang tight' : 'Drag & drop, or click (PNG or JPG, up to 10MB)'}
          </span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="pointer-events-none fixed top-0 left-0 h-px w-px opacity-0"
            onChange={e => {
              void handleFiles(e.target.files)
              if (inputRef.current) inputRef.current.value = ''
            }}
          />
        </label>

        {error && <div className="mt-2 text-[11px] text-red-400">{error}</div>}

        {/* Brand library — only when the business actually has photos. */}
        {images.length > 0 ? (
          <>
            <div className="mt-3 mb-2 flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-paper-faint uppercase">
              <ImagePlus size={12} />
              From your brand photos
            </div>
            <div className="grid max-h-[200px] grid-cols-4 gap-2 overflow-y-auto pr-0.5">
              {images.map(img => (
                <ImagePickerTile
                  key={img.attachmentId}
                  src={img.publicUrl!}
                  onClick={() => pick(img.attachmentId, img.publicUrl)}
                  className="h-[62px] w-full"
                />
              ))}
            </div>
          </>
        ) : (
          <div className="mt-2.5 text-[11px] leading-relaxed text-paper-faint">
            Photos you add to your brand kit show up here so you can reuse them in one tap.
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
